import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scale } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { US_STATES, STATUS_STYLES } from "@/data/states";

const CAP = "https://images.unsplash.com/photo-1557160854-e1e89fdd3286?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

export default function Legislation() {
  useSeo({ title: "Legislation Center", description: "State-by-state wolfdog ownership legislation." });
  const [byCode, setByCode] = useState({});

  useEffect(() => {
    api.get("/legislation").then((r) => {
      const map = {};
      r.data.forEach((l) => (map[l.state_code] = l));
      setByCode(map);
    }).catch(() => {});
  }, []);

  const summary = [
    { label: "Legal", key: "Legal" },
    { label: "Legal w/ Permit", key: "Legal w/ Permit" },
    { label: "Illegal (Grandfathered)", key: "Illegal (Grandfathered)" },
    { label: "Illegal", key: "Illegal" },
  ];
  const counts = Object.values(byCode).reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  const states = [...US_STATES].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div>
      <div className="relative h-72 bg-[#0A192F]">
        <img src={CAP} alt="Capitol" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <div className="flex items-center gap-3 mb-3">
              <Scale className="w-7 h-7 text-[#D4AF37]" />
              <span className="overline text-[#D4AF37]">State-by-State</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
              Legislation Center
            </h1>
            <p className="mt-3 text-white/70 max-w-2xl">
              Track the laws that impact wolfdog owners across the country. Select a state for detailed information.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="legislation-summary">
          {summary.map((s) => (
            <div key={s.key} className={`border p-5 ${STATUS_STYLES[s.key]}`} data-testid={`summary-${s.key}`}>
              <p className="text-3xl font-display font-black">{counts[s.key] || 0}</p>
              <p className="overline mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {states.map((st) => {
            const rec = byCode[st.code];
            const status = rec?.status || "No Specific Regulation";
            return (
              <Link
                key={st.code}
                to={`/legislation/${st.code}`}
                data-testid={`state-${st.code}`}
                className="group bg-white border border-[#0A192F]/10 p-4 hover:-translate-y-1 hover:border-[#D4AF37] transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-[#0A192F] group-hover:text-[#8B6D1F] transition-colors">
                    {st.name}
                  </span>
                  <span className="text-xs text-[#4A5568]">{st.code}</span>
                </div>
                <span className={`inline-block mt-3 text-[0.65rem] px-2 py-1 border rounded-full ${STATUS_STYLES[status]}`}>
                  {status}
                </span>
              </Link>
            );
          })}
        </div>

        {/* Wolfdog definition */}
        <div className="mt-16 bg-[#0A192F] text-white p-8 border-l-4 border-[#D4AF37]" data-testid="wolfdog-definition">
          <h3 className="font-display text-xl font-bold text-[#D4AF37] mb-2">Wolfdog (Hybrid Animal)</h3>
          <p className="text-white/80 leading-relaxed">
            A cross between a gray wolf (Canis lupus) and a domestic dog, with varying levels of wolf ancestry that can
            affect behavior and legal classification.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 border-t border-[#0A192F]/10 pt-6">
          <p className="text-sm text-[#4A5568] leading-relaxed">
            In many states, counties, cities, and towns, it is illegal to own a wolf dog. Even if it is legal, there are
            likely to be restrictions on what percentage wolf can be owned and/or requirements for owners to acquire
            additional permits to keep a wolf dog legally. It is imperative for anyone interested in owning a wolf dog to
            research thoroughly and comply with all laws and permitting requirements at the state and local levels prior
            to acquiring a wolf dog. This information is provided for educational purposes and is not legal advice.
          </p>
        </div>
      </div>
    </div>
  );
}
